import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { BLOG_POSTS } from "@/pages/blog/posts";

export default function Header({ t, language, setLanguage }) {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // "descubre" | "blog" | null
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  // 1. Descubre dropdown items
  const discoverLinks = [
    { to: "/glutation", label: language === "es" ? "Glutatión" : "Glutathione" },
    { to: "/sistema-inmunologico", label: language === "es" ? "Sistema Inmunológico" : "Immune System" },
    { to: "/immunocal", label: "Immunocal" },
    { to: "/immunocal-precio", label: language === "es" ? "Precios y dónde comprar" : "Pricing & where to buy" },
    { to: "/esperanza", label: language === "es" ? "✨ Esperanza" : "✨ Hope", highlight: true },
  ];

  // 2. Blog dropdown — last 4 posts
  const recentPosts = BLOG_POSTS.slice(0, 4);

  // Home anchors
  const homeLink = (anchor) => (onHome ? `#${anchor}` : `/#${anchor}`);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "py-2 bg-[#f9f6f0]/90 backdrop-blur-xl border-b border-[#2c4a3b]/10"
          : "py-3 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
          <div className="w-11 h-11 md:w-12 md:h-12 rounded-full border-[1.5px] border-[#bc6c25] grid place-items-center text-[#bc6c25] font-serif italic text-2xl shrink-0">
            E
          </div>
          <span className="flex flex-col leading-none">
            <span className="font-serif text-xl md:text-2xl tracking-[0.02em] text-[#1a2421]">
              Eliora <em className="not-italic text-[#bc6c25]">Longevity</em>
            </span>
            <span className="text-[9px] tracking-[0.22em] uppercase text-[#5c6b61] mt-1.5 hidden md:block">
              {language === "es" ? "Ciencia · Vitalidad · Vida abundante" : "Science · Vitality · Abundant life"}
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7 text-sm text-[#5c6b61]">
          {/* 1. Descubre dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setOpenMenu("descubre")}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <button
              data-testid="nav-descubre-trigger"
              className="flex items-center gap-1 hover:text-[#1a2421] transition-colors py-2"
              aria-expanded={openMenu === "descubre"}
            >
              {language === "es" ? "Descubre" : "Discover"}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === "descubre" ? "rotate-180" : ""}`} />
            </button>
            {openMenu === "descubre" && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-64 bg-white border border-[#2c4a3b]/10 rounded-2xl shadow-xl overflow-hidden"
                data-testid="nav-descubre-menu"
              >
                {discoverLinks.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    className={`block px-5 py-3 text-sm transition-colors ${
                      p.highlight
                        ? "bg-[#bc6c25]/8 text-[#bc6c25] font-semibold border-t border-[#bc6c25]/15 hover:bg-[#bc6c25]/15"
                        : "text-[#1a2421] hover:bg-[#bc6c25]/8 hover:text-[#bc6c25]"
                    }`}
                  >
                    {p.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* 2. Blog dropdown with 4 latest posts */}
          <div
            className="relative"
            onMouseEnter={() => setOpenMenu("blog")}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <button
              data-testid="nav-blog-trigger"
              className="flex items-center gap-1 hover:text-[#1a2421] transition-colors py-2"
              aria-expanded={openMenu === "blog"}
            >
              Blog
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === "blog" ? "rotate-180" : ""}`} />
            </button>
            {openMenu === "blog" && (
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-80 bg-white border border-[#2c4a3b]/10 rounded-2xl shadow-xl overflow-hidden"
                data-testid="nav-blog-menu"
              >
                <p className="px-5 pt-4 pb-2 text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">
                  {language === "es" ? "Últimos artículos" : "Latest articles"}
                </p>
                {recentPosts.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    className="block px-5 py-2.5 text-sm text-[#1a2421] hover:bg-[#bc6c25]/8 hover:text-[#bc6c25] leading-snug"
                  >
                    {p.title}
                  </Link>
                ))}
                <Link
                  to="/blog"
                  className="block px-5 py-3 mt-1 text-xs font-bold uppercase tracking-[0.18em] text-[#bc6c25] hover:bg-[#bc6c25]/8 border-t border-[#2c4a3b]/8"
                >
                  {language === "es" ? "Ver todos los artículos →" : "View all articles →"}
                </Link>
              </div>
            )}
          </div>

          {/* 3. Kit de Inicio */}
          <a href={homeLink("starter-kit")} className="hover:text-[#1a2421] transition-colors">
            {t.nav.starterKit}
          </a>

          {/* 4. Productos */}
          <a href={homeLink("products")} className="hover:text-[#1a2421] transition-colors">
            {language === "es" ? "Productos" : "Products"}
          </a>

          {/* 5. Preguntas */}
          <a href={homeLink("faq")} className="hover:text-[#1a2421] transition-colors">
            {t.nav.faq}
          </a>

          {/* 6. Home */}
          <Link to="/" className="hover:text-[#1a2421] transition-colors" data-testid="nav-home">
            {language === "es" ? "Inicio" : "Home"}
          </Link>
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-3">
          <div
            className="hidden sm:flex items-center gap-1 bg-[#f0ebe1] rounded-full p-1 text-xs font-bold uppercase tracking-wider text-[#5c6b61]"
            data-testid="language-toggle"
            role="group"
            aria-label="Language toggle"
          >
            {["es", "en"].map((lng) => (
              <button
                key={lng}
                data-testid={`lang-${lng}`}
                onClick={() => setLanguage(lng)}
                className={`px-3 py-1.5 rounded-full transition-all ${
                  language === lng ? "bg-white text-[#1a2421] shadow-sm" : "hover:text-[#1a2421]"
                }`}
                aria-pressed={language === lng}
              >
                {lng}
              </button>
            ))}
          </div>

          <button
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-trigger"
            aria-label="Open menu"
            className="lg:hidden w-10 h-10 rounded-full bg-[#1a2421]/5 grid place-items-center text-[#1a2421]"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Mobile fullscreen menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-[#1a2421] text-[#f9f6f0] flex flex-col" data-testid="mobile-menu">
          <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
            <Link to="/" className="font-serif text-xl">
              Eliora <em className="text-[#bc6c25]">Longevity</em>
            </Link>
            <button
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-close"
              aria-label="Close menu"
              className="w-10 h-10 rounded-full bg-white/5 grid place-items-center"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-6 py-8 space-y-1">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold mb-3">
              {language === "es" ? "Descubre" : "Discover"}
            </p>
            {discoverLinks.map((p) => (
              <Link key={p.to} to={p.to} className="block py-3 text-lg font-serif border-b border-white/10">
                {p.label}
              </Link>
            ))}

            <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold mt-8 mb-3">
              Blog · {language === "es" ? "Últimos artículos" : "Latest articles"}
            </p>
            {recentPosts.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="block py-3 text-base font-serif border-b border-white/10 leading-snug">
                {p.title}
              </Link>
            ))}
            <Link to="/blog" className="block py-3 text-sm font-bold uppercase tracking-[0.18em] text-[#bc6c25] border-b border-white/10">
              {language === "es" ? "Ver todos →" : "View all →"}
            </Link>

            <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold mt-8 mb-3">
              {language === "es" ? "Más" : "More"}
            </p>
            <a href={homeLink("starter-kit")} className="block py-3 text-lg font-serif border-b border-white/10">{t.nav.starterKit}</a>
            <a href={homeLink("products")} className="block py-3 text-lg font-serif border-b border-white/10">{language === "es" ? "Productos" : "Products"}</a>
            <a href={homeLink("faq")} className="block py-3 text-lg font-serif border-b border-white/10">{t.nav.faq}</a>
            <Link to="/" className="block py-3 text-lg font-serif border-b border-white/10">
              {language === "es" ? "Inicio" : "Home"}
            </Link>

            <div className="flex items-center gap-1 bg-white/10 rounded-full p-1 text-xs font-bold uppercase tracking-wider text-[#f9f6f0]/70 w-fit mt-10">
              {["es", "en"].map((lng) => (
                <button
                  key={lng}
                  onClick={() => setLanguage(lng)}
                  className={`px-4 py-2 rounded-full ${language === lng ? "bg-[#bc6c25] text-white" : ""}`}
                >
                  {lng}
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
